/**
 * Formatage des dates et des durées de la console (ré-audit N-12) : un seul
 * formateur, des formats nommés. Chaque écran garde le format qu'il
 * affichait. Aucun écran n'appelle `toLocale…` sur une date.
 */
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

/** Langue d'affichage des dates. */
const DATE_LOCALE = 'fr-FR';

const DATE_PART = { day: '2-digit', month: '2-digit', year: 'numeric' } as const;
const TIME_PART = { hour: '2-digit', minute: '2-digit' } as const;
const SECONDS_PART = { ...DATE_PART, ...TIME_PART, second: '2-digit' } as const;

export type DateFormat =
  | 'date' | 'dayMonthShort' | 'dayMonthShortPadded' | 'dateTime' | 'dateTimeSeconds' | 'dateTimeSecondsUtc'
  | 'dateTimeLong' | 'weekdayDateTimeSeconds' | 'shortDateTime' | 'mediumDateTime'
  | 'dayMonthLong' | 'dayMonthShortTime' | 'dayMonthTime' | 'monthYear' | 'time' | 'timeSeconds';

/** Formats nommés (exemples pour le 4 octobre 2026, 14 h 03 min 22 s). */
const DATE_FORMATS: Record<DateFormat, Intl.DateTimeFormatOptions> = {
  /** 04/10/2026 */
  date: DATE_PART,
  /** 4 oct. 2026 */
  dayMonthShort: { day: 'numeric', month: 'short', year: 'numeric' },
  /** 04 oct. 2026 */
  dayMonthShortPadded: { day: '2-digit', month: 'short', year: 'numeric' },
  /** 04/10/2026 14:03 */
  dateTime: { ...DATE_PART, ...TIME_PART },
  /** 04/10/2026 14:03:22 */
  dateTimeSeconds: SECONDS_PART,
  /** 04/10/2026 14:03:22, en heure UTC */
  dateTimeSecondsUtc: { ...SECONDS_PART, timeZone: 'UTC' },
  /** 4 octobre 2026 à 14:03 */
  dateTimeLong: { day: 'numeric', month: 'long', year: 'numeric', ...TIME_PART },
  /** dimanche 04 octobre 2026 à 14:03:22 */
  weekdayDateTimeSeconds: { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', ...TIME_PART, second: '2-digit' },
  /** 04/10/2026 14:03 (styles Intl courts) */
  shortDateTime: { dateStyle: 'short', timeStyle: 'short' },
  /** 4 oct. 2026, 14:03 */
  mediumDateTime: { dateStyle: 'medium', timeStyle: 'short' },
  /** 4 octobre 2026 */
  dayMonthLong: { day: 'numeric', month: 'long', year: 'numeric' },
  /** 4 oct. 2026, 14:03 (options explicites) */
  dayMonthShortTime: { day: 'numeric', month: 'short', year: 'numeric', ...TIME_PART },
  /** 04/10 14:03 */
  dayMonthTime: { day: '2-digit', month: '2-digit', ...TIME_PART },
  /** octobre 2026 */
  monthYear: { month: 'long', year: 'numeric' },
  /** 14:03 */
  time: TIME_PART,
  /** 14:03:22 */
  timeSeconds: { hour: 'numeric', minute: 'numeric', second: 'numeric' },
};

/** Date ISO, objet `Date`, ou absente. */
export type DateInput = string | Date | null | undefined;

/**
 * Formate `value` au format nommé `format` ; `empty` si la date est absente.
 * Une date illisible s'affiche comme le fait `Intl` (« Invalid Date »).
 */
export const formatDate = (value: DateInput, format: DateFormat = 'date', empty = '—'): string =>
  value ? new Date(value).toLocaleString(DATE_LOCALE, DATE_FORMATS[format]) : empty;

/** Comme `formatDate`, mais `empty` aussi pour une date illisible. */
export const formatValidDate = (value: DateInput, format: DateFormat = 'date', empty = '—'): string => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString(DATE_LOCALE, DATE_FORMATS[format]) : empty;
};

/**
 * Date relative (« il y a 3 heures ») ; `empty` sans date ; la valeur reçue
 * telle quelle si elle est illisible.
 */
export const formatRelativeDate = (value: string | null | undefined, empty = '—'): string => {
  if (!value) return empty;
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true, locale: fr });
  } catch {
    return value;
  }
};

/** Secondes par minute et par heure. */
const MINUTE_S = 60;
const HOUR_S = 3600;

/**
 * Durée en secondes : « 45s », « 12min », « 1h 5min ». `minuteUnit` est
 * l'unité des minutes seules (« min », précédé ou non d'une espace selon
 * l'écran).
 */
export const formatDuration = (seconds: number, minuteUnit = 'min'): string => {
  if (seconds < MINUTE_S) return `${seconds}s`;
  if (seconds < HOUR_S) return `${Math.floor(seconds / MINUTE_S)}${minuteUnit}`;
  const hours = Math.floor(seconds / HOUR_S);
  const minutes = Math.floor((seconds % HOUR_S) / MINUTE_S);
  return `${hours}h ${minutes}min`;
};
